import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Percent,
  Store,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import AdminGroupedPaymentsTable from '../../components/admin/AdminGroupedPaymentsTable';
import DatePicker from '../../components/client/DatePicker';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { useAdminPaymentStatistics } from '../../services/adminTransactionService';
import { formatDate } from '../../utils/dateUtils';
import {
  formatAdminMoney,
  getVendorPayout,
  getPlatformCommission,
  sumVendorPayout,
  sumCommission,
  groupPaymentsByBooking,
} from '../../utils/adminPaymentTotals';

const AdminPayments = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    date_from: '',
    date_to: '',
  });

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
  ];

  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined
    )
  );

  const { data: statisticsResponse } = useAdminPaymentStatistics(cleanFilters);

  const { data: paymentsResponse, isLoading, error } = useQuery({
    queryKey: ['adminPayments', cleanFilters],
    queryFn: async () => {
      const params = new URLSearchParams(cleanFilters);
      params.append('per_page', '1000');
      const response = await api.get(`/admin/payments?${params.toString()}`);
      return response.data;
    },
  });

  const allPayments = useMemo(
    () => paymentsResponse?.data?.data || paymentsResponse?.data || [],
    [paymentsResponse]
  );

  const payments = useMemo(() => {
    const filtered = allPayments.filter((payment) => payment.status !== 'pending');
    return groupPaymentsByBooking(filtered);
  }, [allPayments]);

  const getStatusConfig = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' },
      refunded: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Refunded' },
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const columns = [
    {
      key: 'order',
      label: 'Order',
      sortable: true,
      width: 'w-44',
      render: (payment) => {
        const bookingRef = payment.booking?.booking_reference || payment.booking_id || 'N/A';
        const vendorName = payment.booking?.vendor?.business_name || 'Unknown vendor';
        return (
          <div className="min-w-0">
            {payment.booking_id ? (
              <button
                type="button"
                onClick={() => navigate(`/admin/bookings/${payment.booking_id}`)}
                className="text-sm font-medium text-primary-600 hover:underline truncate block"
              >
                #{bookingRef}
              </button>
            ) : (
              <span className="text-sm text-gray-400">N/A</span>
            )}
            <p className="text-xs text-gray-500 truncate">{vendorName}</p>
          </div>
        );
      },
    },
    {
      key: 'amount',
      label: 'Paid',
      sortable: true,
      width: 'w-28',
      render: (payment) => (
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">${formatAdminMoney(payment.amount)}</p>
          {payment.payment_type && (
            <p className="text-xs text-gray-500">{payment.payment_type}</p>
          )}
        </div>
      ),
    },
    {
      key: 'platform_fee',
      label: 'Commission',
      sortable: true,
      width: 'w-24',
      render: (payment) => {
        const fee = getPlatformCommission(payment);
        return (
          <p className="text-sm font-medium text-right text-indigo-600">
            {fee > 0 ? `-$${formatAdminMoney(fee)}` : '$0.00'}
          </p>
        );
      },
    },
    {
      key: 'vendor_payout',
      label: 'To vendor',
      sortable: true,
      width: 'w-28',
      render: (payment) => {
        const payout = getVendorPayout(payment);
        return (
          <p
            className={`text-sm font-medium text-right ${
              payout > 0 ? 'text-emerald-600' : 'text-gray-400'
            }`}
          >
            ${formatAdminMoney(payout)}
          </p>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-28',
      render: (payment) => (
        <span className="text-sm text-gray-700">{formatDate(payment.created_at, 'medium')}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (payment) => {
        const statusConfig = getStatusConfig(payment.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
          >
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusConfig.label}
          </span>
        );
      },
    },
  ];

  const actions = [
    {
      label: 'View Booking',
      icon: Eye,
      variant: 'default',
      onClick: (payment) => {
        if (payment.booking_id) {
          navigate(`/admin/bookings/${payment.booking_id}`);
        }
      },
    },
  ];

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    const q = searchTerm.toLowerCase();
    return data.filter(
      (payment) =>
        payment.booking?.booking_reference?.toLowerCase().includes(q) ||
        payment.booking?.vendor?.business_name?.toLowerCase().includes(q) ||
        payment.booking?.client?.name?.toLowerCase().includes(q)
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const apiStats = statisticsResponse?.data;
  const completed = allPayments.filter((p) => p.status === 'completed');
  const totalCollected = apiStats?.total_amount ?? completed.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const accumulatedCommission = apiStats?.accumulated_commission ?? sumCommission(completed);
  const totalVendorPayout = apiStats?.total_vendor_payout ?? sumVendorPayout(completed);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Payments</h3>
          <p className="text-gray-600">
            {error?.response?.data?.message || 'Failed to load payments. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6" />
            Payments
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Client payments, platform commission, and vendor share
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <p className="text-sm text-gray-600">Total collected</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">${formatAdminMoney(totalCollected)}</p>
            <p className="text-xs text-gray-500 mt-0.5">From clients (completed)</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Commission (deduction)</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">
                  -${formatAdminMoney(accumulatedCommission)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Platform share</p>
              </div>
              <Percent className="w-8 h-8 text-indigo-500 flex-shrink-0" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">To vendors</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  ${formatAdminMoney(totalVendorPayout)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">After commission</p>
              </div>
              <Store className="w-8 h-8 text-emerald-500 flex-shrink-0" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SearchSelect
              label="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={statusOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <AdminGroupedPaymentsTable
            data={payments}
            columns={columns}
            actions={actions}
            loading={isLoading}
            getStatusConfig={getStatusConfig}
            searchPlaceholder="Search by booking or vendor..."
            onSearch={handleSearch}
            emptyMessage="No payments found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;
